/**
 * Lifecycle-driven action bar. Renders sticky-bottom on mobile and inline on
 * desktop (CheckInDesktopActionGroup, dropped into the page header). On-system
 * buttons (sm4rt-web-ui skill) — see .ci-btn in checkInSession.css.
 */

import React from 'react';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import { IconDotsVertical } from '@tabler/icons-react';
import type { CheckInSession } from '@techgitter/contracts';

import { deriveActionFlags } from './sessionDetailHelpers';

export interface CheckInActionBarProps {
  session: CheckInSession;
  onMarkPaid: () => void;
  onDeferPayment: () => void;
  /** Opens the printed-terms dialog. Optional so older callers keep compiling. */
  onContinueWithoutDigitalAcceptance?: () => void;
  onRetry: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onRecover: () => void;
  onSendToWall: () => void;
  onCreateTickets: () => void;
  /** True while the send request is in flight. */
  isSendingToWall?: boolean;
  /** True while the Create Tickets request is in flight. */
  isCreatingTickets?: boolean;
}

export const CheckInActionBar: React.FC<CheckInActionBarProps> = props => {
  const buttons = collectButtons(props);

  if (buttons.length === 0) {
    return (
      <div className="checkin-sticky-bottom-bar checkin-actionbar--mobile">
        <div className="ci-actionbar ci-centre">
          <span className="ci-actionbar__empty">
            {props.session.lifecycleStatus === 'checked_in'
              ? 'Session complete'
              : props.session.lifecycleStatus === 'cancelled'
              ? 'Session was cancelled'
              : 'No actions available'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin-sticky-bottom-bar checkin-actionbar--mobile">
      <div className="ci-actionbar ci-centre">
        <ActionButtonsRow buttons={buttons} />
      </div>
    </div>
  );
};

export const CheckInDesktopActionGroup: React.FC<CheckInActionBarProps> = props => {
  const buttons = collectButtons(props);
  if (buttons.length === 0) return null;
  return (
    <div className="ci-actionbar">
      <ActionButtonsRow buttons={buttons} compact />
    </div>
  );
};

// ---------------------------------------------------------------------------

interface ActionDef {
  id: string;
  label: string;
  /** On-system button tone class suffix. */
  tone: 'primary' | 'success' | 'warning' | 'ghost' | 'danger';
  primary: boolean;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
}

/**
 * Whether this check-in still has something for a customer to scan.
 *
 * Mirrors the server's refusals rather than duplicating business rules: a
 * cancelled or completed session's QR would lead nowhere, and once the terms
 * are accepted a scan just says "you're already done", which reads as a fault.
 * The server is still the authority — this only keeps a button off the screen
 * that would always fail.
 */
function canSendToWall(session: CheckInSession): boolean {
  // Express is for established business customers — there is no customer
  // intake step, so nothing to scan (the API refuses it too).
  if (session.intakeType === 'express') return false;
  const dead = ['cancelled', 'expired', 'checked_in'];
  if (dead.includes(session.lifecycleStatus)) return false;
  return !session.termsAcceptance;
}

/**
 * Whether to offer Create Tickets, and why it might be disabled.
 *
 * The rule itself lives on the server (`ticketCreation` on the session) so the
 * button and the endpoint cannot disagree — this only decides whether to put it
 * on screen. Shown whenever a captured device still has no RepairShopr ticket,
 * including disabled-with-a-reason, because "why can't I raise the job?" is the
 * question reception actually has. Hidden when there is nothing to raise.
 */
function ticketCreationAction(
  props: CheckInActionBarProps,
): ActionDef | null {
  const state = props.session.ticketCreation;
  if (!state || state.pendingDraftJobCount < 1) return null;
  if (['cancelled', 'expired'].includes(props.session.lifecycleStatus)) return null;

  return {
    id: 'create-tickets',
    label: props.isCreatingTickets ? 'Creating…' : 'Create Tickets',
    tone: 'primary',
    primary: true,
    disabled: props.isCreatingTickets || !state.canCreate,
    tooltip:
      state.reason
      ?? 'Raise the RepairShopr ticket for the captured device(s). The inspection does not need to be finished.',
    onClick: props.onCreateTickets,
  };
}

function collectButtons(props: CheckInActionBarProps): ActionDef[] {
  const flags = deriveActionFlags(props.session);
  const out: ActionDef[] = [];

  if (flags.canRetry) {
    out.push({ id: 'retry', label: 'Retry', tone: 'warning', primary: true, onClick: props.onRetry });
  }
  // The server decides (`continueWithoutDigitalAcceptance.available`) — the
  // same rule its endpoint enforces — so this only puts it on screen.
  if (props.session.continueWithoutDigitalAcceptance?.available && props.onContinueWithoutDigitalAcceptance) {
    out.push({
      id: 'continue-without-digital-acceptance',
      label: 'Continue without digital acceptance',
      tone: 'ghost',
      primary: false,
      onClick: props.onContinueWithoutDigitalAcceptance,
    });
  }
  // Ahead of Mark Paid and Complete: both come later in the counter flow, and
  // completion now requires a real ticket on every device.
  const createTickets = ticketCreationAction(props);
  if (createTickets) {
    out.push(createTickets);
  }
  if (flags.canMarkPaid) {
    out.push({ id: 'mark-paid', label: 'Mark Paid', tone: 'primary', primary: true, onClick: props.onMarkPaid });
  }
  if (flags.canDeferPayment) {
    out.push({ id: 'defer', label: 'Defer / Not Required', tone: 'ghost', primary: false, onClick: props.onDeferPayment });
  }
  if (flags.canComplete) {
    out.push({ id: 'complete', label: 'Complete Check-In', tone: 'success', primary: true, onClick: props.onComplete });
  } else if (flags.ticketAssignmentBlocking) {
    out.push({
      id: 'complete',
      label: 'Complete Check-In',
      tone: 'success',
      primary: true,
      disabled: true,
      // For everything but express the blocker is a device with no RS ticket
      // yet, which Create Tickets resolves — so say that rather than pointing
      // at the picker, which cannot fix it.
      tooltip: createTickets
        ? 'Create the RepairShopr tickets for all devices first'
        : 'Resolve ticket assignment for all devices first',
      onClick: props.onComplete,
    });
  }
  if (flags.canRecover) {
    out.push({ id: 'recover', label: 'Recover', tone: 'primary', primary: true, onClick: props.onRecover });
  }
  if (canSendToWall(props.session)) {
    out.push({
      id: 'send-to-wall',
      label: props.isSendingToWall ? 'Sending…' : 'QR to wall',
      tone: 'ghost',
      primary: false,
      disabled: props.isSendingToWall,
      tooltip:
        'Show this check-in\u2019s QR code on the reception TV so the customer can enter their own details',
      onClick: props.onSendToWall,
    });
  }
  if (flags.canCancel) {
    out.push({ id: 'cancel', label: 'Cancel', tone: 'danger', primary: false, onClick: props.onCancel });
  }

  return out;
}

const ActionButtonsRow: React.FC<{ buttons: ActionDef[]; compact?: boolean }> = ({ buttons, compact }) => {
  const [overflowAnchor, setOverflowAnchor] = React.useState<HTMLElement | null>(null);
  // Keep the first few visible; the rest go into the overflow menu.
  const visibleLimit = compact ? 3 : 2;
  const visible = buttons.slice(0, visibleLimit);
  const overflow = buttons.slice(visibleLimit);

  return (
    <>
      {visible.map(btn => {
        const className = [
          'ci-btn',
          `ci-btn--${btn.tone}`,
          compact ? 'ci-btn--sm' : 'ci-btn--block',
        ].join(' ');
        const button = (
          <button key={btn.id} type="button" className={className} onClick={btn.onClick} disabled={btn.disabled}>
            {btn.label}
          </button>
        );
        return btn.tooltip ? (
          <Tooltip key={btn.id} title={btn.tooltip} arrow>
            {/* span wrapper so Tooltip works with disabled buttons */}
            <span style={{ width: compact ? undefined : '100%' }}>{button}</span>
          </Tooltip>
        ) : button;
      })}
      {overflow.length > 0 && (
        <>
          <button
            type="button"
            className={`ci-btn ci-btn--ghost${compact ? ' ci-btn--sm' : ''}`}
            onClick={e => setOverflowAnchor(e.currentTarget)}
            aria-label="More actions"
          >
            <IconDotsVertical size={16} stroke={1.5} />
          </button>
          <Menu
            anchorEl={overflowAnchor}
            open={Boolean(overflowAnchor)}
            onClose={() => setOverflowAnchor(null)}
          >
            {overflow.map(btn => (
              <MenuItem
                key={btn.id}
                onClick={() => {
                  setOverflowAnchor(null);
                  btn.onClick();
                }}
              >
                {btn.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </>
  );
};
